import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQueries } from '@tanstack/react-query';
import { getAuthCookies, getUserFromToken } from '@webmotors/consumer-shared-auth';
import { getUserFacingApiErrorMessage } from '@/utils/api-error-message.helpers';
import { MY_ADS_TOAST } from './utils/toast-messages.constants';
import { useAdsList, useToggleAdStatus, useDeleteAd, adsListingService } from '@/hooks/api/ads-listing';
import { plansUpgradeService } from '@/hooks/api/plans-upgrade';
import { advertiserService } from '@/hooks/api/advertiser';
import { useAnalytics } from '@/hooks/analytics';
import { useToast } from '@/components/toast';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { ENV } from '@/app/config';
import { dispatchAsync, openCopiChat } from '@/utils';
import { TAB_IDS, TAB_CONFIG, TAB_ORDER, STATUS_IDS, MENU_IDS, getBuyUrl, buildTabPageView, buildAdEvent } from './utils';
import type { TUseMyAdsPageReturn, TDeactivateModalState, TMarkAsSoldModalState, TEditModalState, TUpgradeModalState, TDeleteModalState } from './my-ads.types';
import type { TTabId, TActionId, TStatusId, TMenuActionId } from './utils';
import type { TVehicleCardData } from './components/vehicle-card';
import type { TAdListItem, TVehicleType } from '@/hooks/api/ads-listing';

/**
 * Mapa das descrições de status devolvidas pela API (normalizadas) para os
 * identificadores internos exibidos pelo card. ANCR-5098: antes três status
 * ("indisponível", "falha na transação", "pagamento via app iOS") eram
 * colapsados em INACTIVE — agora cada um tem seu próprio identificador para
 * a badge e a ação refletirem o estado real do anúncio.
 */
const AD_STATUS_DESCRIPTION_MAP: Record<string, TStatusId> = {
	'anuncio ativo': STATUS_IDS.ACTIVE,
	'anuncio em analise': STATUS_IDS.PENDING,
	'anuncio desativado': STATUS_IDS.INACTIVE,
	'anuncio indisponivel': STATUS_IDS.UNAVAILABLE,
	'falha na transacao': STATUS_IDS.TRANSACTION_FAILED,
	'a confirmar pagamento': STATUS_IDS.PAYMENT_PENDING,
	'dados incompletos': STATUS_IDS.INCOMPLETE,
	'pagamento nao autorizado': STATUS_IDS.PAYMENT_UNAUTHORIZED,
	'pagamento disponivel apenas pelo aplicativo ios': STATUS_IDS.IOS_PAYMENT_ONLY
};

const normalize = (str: string) =>
	str.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const mapApiStatusToDisplayStatus = (ad: TAdListItem): TStatusId =>
	AD_STATUS_DESCRIPTION_MAP[normalize(ad.adStatusDescription)] ?? STATUS_IDS.ACTIVE;


const mapAdToCardData = (ad: TAdListItem): TVehicleCardData => ({
	...ad,
	displayStatus: mapApiStatusToDisplayStatus(ad),
	interestedCount: ad.proposals
});

const INITIAL_DEACTIVATE_STATE: TDeactivateModalState = {
	isOpen: false,
	adId: null,
	vehicleType: 'car',
	adTitle: '',
	isLoading: false
};

const INITIAL_MARK_AS_SOLD_STATE: TMarkAsSoldModalState = {
	isOpen: false,
	adId: null,
	vehicleType: 'car',
	adTitle: '',
	isLoading: false
};

const INITIAL_EDIT_STATE: TEditModalState = {
	isOpen: false,
	adId: null,
	vehicleType: 'car'
};

const INITIAL_UPGRADE_STATE: TUpgradeModalState = {
	isOpen: false,
	adId: null,
	vehicleType: 'car'
};

const INITIAL_DELETE_STATE: TDeleteModalState = {
	isOpen: false,
	adId: null,
	vehicleType: 'car',
	adTitle: '',
	isLoading: false
};

const useMyAdsPage = (): TUseMyAdsPageReturn => {
	const [activeTab, setActiveTab] = useState<TTabId>(TAB_IDS.ACTIVE);
	const [deactivateModal, setDeactivateModal] = useState<TDeactivateModalState>(INITIAL_DEACTIVATE_STATE);
	const [markAsSoldModal, setMarkAsSoldModal] = useState<TMarkAsSoldModalState>(INITIAL_MARK_AS_SOLD_STATE);
	const [editModal, setEditModal] = useState<TEditModalState>(INITIAL_EDIT_STATE);
	const [upgradeModal, setUpgradeModal] = useState<TUpgradeModalState>(INITIAL_UPGRADE_STATE);
	const [deleteModal, setDeleteModal] = useState<TDeleteModalState>(INITIAL_DELETE_STATE);
	const { trackPageView, trackEvent, isProfileReady } = useAnalytics();
	const { addToast } = useToast();
	const isMobile = useIsMobile();

	const tabConfig = TAB_CONFIG[activeTab];

	const carQuery = useAdsList({
		vehicleType: 'car',
		params: { status: tabConfig.statusFilter },
		enabled: true
	});

	const bikeQuery = useAdsList({
		vehicleType: 'bike',
		params: { status: tabConfig.statusFilter },
		enabled: true
	});

	const { toggleStatusAsync: toggleCarStatusAsync } = useToggleAdStatus({ vehicleType: 'car' });
	const { toggleStatusAsync: toggleBikeStatusAsync } = useToggleAdStatus({ vehicleType: 'bike' });
	const { deleteAdAsync: deleteCarAdAsync } = useDeleteAd({ vehicleType: 'car' });
	const { deleteAdAsync: deleteBikeAdAsync } = useDeleteAd({ vehicleType: 'bike' });

	const toggleStatusFor = useCallback(
		(vehicleType: TVehicleType) =>
			vehicleType === 'bike' ? toggleBikeStatusAsync : toggleCarStatusAsync,
		[toggleCarStatusAsync, toggleBikeStatusAsync]
	);

	const deleteAdFor = useCallback(
		(vehicleType: TVehicleType) =>
			vehicleType === 'bike' ? deleteBikeAdAsync : deleteCarAdAsync,
		[deleteCarAdAsync, deleteBikeAdAsync]
	);

	const rawAds = useMemo(
		() =>
			[...carQuery.ads, ...bikeQuery.ads].sort((a, b) =>
				(b.createdAt ?? '').localeCompare(a.createdAt ?? '')
			),
		[carQuery.ads, bikeQuery.ads]
	);

	const total = carQuery.total + bikeQuery.total;
	const isLoading = carQuery.isLoading || bikeQuery.isLoading;
	const isPending = carQuery.isPending || bikeQuery.isPending;
	const isFetching = carQuery.isFetching || bikeQuery.isFetching;
	const isPlaceholderData = carQuery.isPlaceholderData || bikeQuery.isPlaceholderData;
	const isListLoading =
		isPending || isLoading || isPlaceholderData || (isFetching && rawAds.length === 0);
	const isError = carQuery.isError && bikeQuery.isError;

	const refetch = useCallback(() => {
		void carQuery.refetch();
		void bikeQuery.refetch();
	}, [carQuery, bikeQuery]);

	const mappedAds = useMemo(() => rawAds.map(mapAdToCardData), [rawAds]);

	const activeAds = useMemo(
		() => mappedAds.filter((ad) => ad.displayStatus === STATUS_IDS.ACTIVE),
		[mappedAds]
	);

	const upgradeQueries = useQueries({
		queries: activeAds.map((ad) => {
			const adVehicleType: TVehicleType = ad.vehicleType ?? 'car';
			return {
				queryKey: ['plans', 'upgrade', adVehicleType, ad.id],
				queryFn: () => plansUpgradeService.listUpgradeModalities(adVehicleType, ad.id),
				staleTime: 5 * 60_000,
				enabled: activeAds.length > 0 && adVehicleType === 'car'
			};
		})
	});

	const isUpgradeCheckPending = activeAds.length > 0 &&
		upgradeQueries.some((q) => q.isLoading);

	const upgradeEligibilityMap = useMemo(() => {
		const map = new Map<number, boolean>();
		activeAds.forEach((ad, index) => {
			const adVehicleType: TVehicleType = ad.vehicleType ?? 'car';
			if (adVehicleType === 'bike') {
				map.set(ad.id, false);
				return;
			}
			const query = upgradeQueries[index];
			if (query?.isSuccess) {
				map.set(ad.id, (query.data?.length ?? 0) > 0);
			}
		});
		return map;
	}, [activeAds, upgradeQueries]);

	const ads = useMemo(
		() => mappedAds.map((ad) => ({
			...ad,
			upgradeEligible: upgradeEligibilityMap.get(ad.id)
		})),
		[mappedAds, upgradeEligibilityMap]
	);

	const isEmpty = !isListLoading && !isError && ads.length === 0;

	const tabCountQueries = useQueries({
		queries: TAB_ORDER.flatMap((tabId) => [
			{
				queryKey: ['ads', 'tab-count', 'car', TAB_CONFIG[tabId].statusFilter],
				queryFn: () => adsListingService.listAds('car', { status: TAB_CONFIG[tabId].statusFilter, limit: 1 }),
				staleTime: 60_000
			},
			{
				queryKey: ['ads', 'tab-count', 'bike', TAB_CONFIG[tabId].statusFilter],
				queryFn: () => adsListingService.listAds('bike', { status: TAB_CONFIG[tabId].statusFilter, limit: 1 }),
				staleTime: 60_000
			}
		])
	});

	const isTabCountsReady = tabCountQueries.every((query) => query.isFetched);

	const tabCounts = useMemo(() =>
		TAB_ORDER.reduce((acc, tabId, index) => {
			const carCount = tabCountQueries[index * 2]?.data?.totalCount ?? 0;
			const bikeCount = tabCountQueries[index * 2 + 1]?.data?.totalCount ?? 0;
			acc[tabId] = carCount + bikeCount;
			return acc;
		}, {} as Record<TTabId, number>),
		[tabCountQueries]
	);

	const findAd = useCallback(
		(adId: number) => ads.find((a) => a.id === adId),
		[ads]
	);

	// Pageview inicial: a aba padrão é "Ativos", então dispara o mesmo evento
	// que a garagem manda ao montar (`meus-anuncios-ativos`). Dispara UMA única
	// vez, após o perfil do anunciante estabilizar (assim o bloco `user` já vai
	// preenchido). A guarda de ref evita o disparo duplicado causado pela
	// mudança de identidade de `trackPageView` quando `profile` carrega e pelo
	// double-invoke do StrictMode. Trocas de aba são cobertas por `handleTabChange`.
	const initialPageViewFiredRef = useRef(false);
	useEffect(() => {
		if (initialPageViewFiredRef.current || !isProfileReady) return;
		initialPageViewFiredRef.current = true;
		trackPageView(buildTabPageView(TAB_IDS.ACTIVE, false));
	}, [isProfileReady, trackPageView]);

	// eventNPSVender: a garagem dispara um CustomEvent + grava `WMGarageNpsData`
	// no localStorage com as datas de criação dos anúncios ATIVOS, assim que a
	// lista carrega (Tabs/index.jsx). Replicamos aqui: a aba padrão é "Ativos",
	// então na primeira carga `activeAds` já reflete os ativos. Dispara uma vez.
	const npsFiredRef = useRef(false);
	useEffect(() => {
		if (npsFiredRef.current) return;
		if (activeTab !== TAB_IDS.ACTIVE || isListLoading) return;
		npsFiredRef.current = true;
		const dataAnuncioCriacao = activeAds
			.map((ad) => ad.createdAt?.split('T')[0])
			.filter((date): date is string => Boolean(date));
		window.localStorage.setItem('WMGarageNpsData', JSON.stringify({ dataAnuncioCriacao }));
		document.dispatchEvent(new CustomEvent('eventNPSVender', { detail: dataAnuncioCriacao }));
	}, [activeTab, isListLoading, activeAds]);

	const handleTabChange = useCallback(
		(tab: TTabId) => {
			setActiveTab(tab);
			// Cada clique de aba ("Ativos", "Em análise", "Inativos",
			// "Incompletos") dispara um novo pageview — paridade com a garagem.
			trackPageView(buildTabPageView(tab, true));
		},
		[trackPageView]
	);

	const runAdAction = useCallback(
		async (adId: number, action: TActionId) => {
			const ad = findAd(adId);
			const vehicleType: TVehicleType = ad?.vehicleType ?? 'car';
			switch (action) {
				case 'markAsSold': {
					const adTitle = ad ? `${ad.brandName} ${ad.modelName}` : '';
					setMarkAsSoldModal({ isOpen: true, adId, vehicleType, adTitle, isLoading: false });
					break;
				}
				case 'reactivate':
					try {
						await toggleStatusFor(vehicleType)({ id: adId, status: 'active' });
						refetch();
						addToast({ type: 'success', title: MY_ADS_TOAST.SUCCESS.AD_REACTIVATED });
						trackEvent('ativarAnuncio', buildAdEvent(activeTab, 'ativarAnuncio', { id: String(adId) }));
					} catch (err) {
						addToast({
							type: 'error',
							title: getUserFacingApiErrorMessage(err, MY_ADS_TOAST.ERROR.REACTIVATE)
						});
					}
					break;
				case 'boost':
					setUpgradeModal({
						isOpen: true,
						adId,
						vehicleType,
						photoUrl: ad?.photoUrl,
						listingAd: ad
					});
					break;
				case 'complete': {
					// `webmotors.vender` atual roda com basename `/vender-carro` ou `/vender-moto`
					// (helpers/router/history.ts), então a página de retomar anúncio fica em
					// `${URL_PORTAL}/${basename}/escolha`.
					//
					// `redir=<emailHash>` é OBRIGATÓRIO — `webmotors.vender` valida o hash em
					// `pages/incompletes/useUtils.ts:56`. Sem ele, o portal entra em loop ou
					// mostra tela vazia (history.push('/escolha') indefinidamente).
					// Por isso aqui falhamos rápido se o hash não puder ser obtido — melhor
					// avisar o usuário do que mandá-lo para uma página quebrada.
					const crmParameters = 'idcmpint=t1:c17:m07:ambiente-usuario:meus_anuncios_incompletos::retomar:vender';
					const sellAppBasename = vehicleType === 'bike' ? 'vender-moto' : 'vender-carro';
					const sellBaseUrl = `${ENV.URL_PORTAL}/${sellAppBasename}`;
					try {
						const { idToken } = getAuthCookies();
						const user = idToken ? getUserFromToken(idToken) : null;
						if (!user?.email) {
							throw new Error('e-mail do usuário não disponível para retomar o anúncio');
						}
						const emailHash = await advertiserService.getEmailHash(user.email);
						if (!emailHash) {
							throw new Error('hash de e-mail vazio retornado pela API');
						}
						window.location.href = `${sellBaseUrl}/escolha?redir=${emailHash}&${crmParameters}`;
					} catch (err) {
						addToast({
							type: 'error',
							title: getUserFacingApiErrorMessage(err, MY_ADS_TOAST.ERROR.RESUME_INCOMPLETE)
						});
					}
					break;
				}
				case 'pay': {
					// ANCR-5410: pagamento de anúncio inativo (não autorizado / a confirmar)
					// vai DIRETO pro checkout do `vender` legado — NÃO passa pela tela de
					// modalidades. O backend retorna `productId` no GET detail; redirecionamos
					// pra `${URL_PORTAL}/vender-{carro|moto}/pagamento` com `advertId`+`productId`
					// +`origin=garagem-pagamento`. O `vender` faz o bootstrap do estado
					// (planos/WMUserData/Redux) a partir desses params e esconde o "Voltar"
					// (pra não cair em modalidades). Mesma origem (webmotors.com.br) → auth via cookie.
					try {
						const detail = await adsListingService.getAdDetail(vehicleType, adId);
						if (!detail.productId) {
							throw new Error('productId não retornado no detalhe do anúncio');
						}
						const sellAppBasename = vehicleType === 'bike' ? 'vender-moto' : 'vender-carro';
						const params = new URLSearchParams({
							advertId: String(adId),
							productId: String(detail.productId),
							origin: 'garagem-pagamento'
						});
						window.location.href = `${ENV.URL_PORTAL}/${sellAppBasename}/pagamento?${params.toString()}`;
					} catch (err) {
						addToast({
							type: 'error',
							title: getUserFacingApiErrorMessage(err, MY_ADS_TOAST.ERROR.PAYMENT_CHECKOUT)
						});
					}
					break;
				}
				case 'contactSupport':
					openCopiChat();
					break;
			}
		},
		[findAd, toggleStatusFor, addToast, refetch, setMarkAsSoldModal, setUpgradeModal, trackEvent, activeTab]
	);

	const openDeactivateModal = useCallback(
		(adId: number) => {
			const ad = findAd(adId);
			const adTitle = ad ? `${ad.brandName} ${ad.modelName}` : '';
			const vehicleType: TVehicleType = ad?.vehicleType ?? 'car';
			setDeactivateModal({ isOpen: true, adId, vehicleType, adTitle, isLoading: false });
		},
		[findAd]
	);

	const closeDeactivateModal = useCallback(() => {
		setDeactivateModal(INITIAL_DEACTIVATE_STATE);
	}, []);

	const runConfirmDeactivate = useCallback(async () => {
		if (!deactivateModal.adId) return;
		setDeactivateModal((prev) => ({ ...prev, isLoading: true }));
		try {
			await toggleStatusFor(deactivateModal.vehicleType)({ id: deactivateModal.adId, status: 'inactive' });
			refetch();
			addToast({ type: 'success', title: MY_ADS_TOAST.SUCCESS.AD_DEACTIVATED });
			trackEvent(
				'desativarAnuncio',
				buildAdEvent(activeTab, 'desativarAnuncio', { id: String(deactivateModal.adId), deactivation: {} })
			);
		} catch (err) {
			addToast({
				type: 'error',
				title: getUserFacingApiErrorMessage(err, MY_ADS_TOAST.ERROR.DEACTIVATE)
			});
		} finally {
			setDeactivateModal(INITIAL_DEACTIVATE_STATE);
		}
	}, [deactivateModal.adId, deactivateModal.vehicleType, toggleStatusFor, refetch, addToast, trackEvent, activeTab]);

	const closeMarkAsSoldModal = useCallback(() => {
		setMarkAsSoldModal(INITIAL_MARK_AS_SOLD_STATE);
	}, []);

	const runConfirmMarkAsSold = useCallback(async () => {
		const { adId, vehicleType } = markAsSoldModal;
		if (!adId) return;

		setMarkAsSoldModal(INITIAL_MARK_AS_SOLD_STATE);

		try {
			await toggleStatusFor(vehicleType)({ id: adId, status: 'inactive' });
			refetch();
			addToast({ type: 'success', title: MY_ADS_TOAST.SUCCESS.AD_MARKED_SOLD });
			// "Já vendi" também desativa o anúncio → mesmo evento da garagem.
			trackEvent(
				'desativarAnuncio',
				buildAdEvent(activeTab, 'desativarAnuncio', { id: String(markAsSoldModal.adId), deactivation: {} })
			);
		} catch (err) {
			addToast({
				type: 'error',
				title: getUserFacingApiErrorMessage(err, MY_ADS_TOAST.ERROR.MARK_SOLD)
			});
		}
	}, [markAsSoldModal.adId, markAsSoldModal.vehicleType, toggleStatusFor, refetch, addToast, trackEvent, activeTab]);

	const openEditModal = useCallback(
		(adId: number) => {
			const ad = findAd(adId);
			const vehicleType: TVehicleType = ad?.vehicleType ?? 'car';
			setEditModal({ isOpen: true, adId, vehicleType });
		},
		[findAd]
	);

	const closeEditModal = useCallback(() => {
		setEditModal(INITIAL_EDIT_STATE);
	}, []);

	const runEditSuccess = useCallback(() => {
		if (editModal.adId) {
			trackEvent(
				'editarAnuncioSucesso',
				buildAdEvent(activeTab, 'editarAnuncioSucesso', { id: String(editModal.adId) })
			);
		}
		refetch();
		setEditModal(INITIAL_EDIT_STATE);
	}, [refetch, trackEvent, activeTab, editModal.adId]);

	const closeUpgradeModal = useCallback(() => {
		// A garagem dispara o pageview de "ativos" ao fechar o modal de upgrade
		// (handleCloseModalPlanUpgrade) — replicamos aqui.
		trackPageView(buildTabPageView(TAB_IDS.ACTIVE, true));
		setUpgradeModal(INITIAL_UPGRADE_STATE);
	}, [trackPageView]);

	const runUpgradeSuccess = useCallback(() => {
		refetch();
		addToast({ type: 'success', title: MY_ADS_TOAST.SUCCESS.PLAN_UPDATED });
	}, [refetch, addToast]);

	const openDeleteModal = useCallback(
		(adId: number) => {
			const ad = findAd(adId);
			const adTitle = ad ? `${ad.brandName} ${ad.modelName}` : '';
			const vehicleType: TVehicleType = ad?.vehicleType ?? 'car';
			setDeleteModal({ isOpen: true, adId, vehicleType, adTitle, isLoading: false });
		},
		[findAd]
	);

	const closeDeleteModal = useCallback(() => {
		setDeleteModal(INITIAL_DELETE_STATE);
	}, []);

	const runConfirmDelete = useCallback(async () => {
		if (!deleteModal.adId) return;
		setDeleteModal((prev) => ({ ...prev, isLoading: true }));
		try {
			await deleteAdFor(deleteModal.vehicleType)({ id: deleteModal.adId });
			refetch();
			addToast({ type: 'success', title: MY_ADS_TOAST.SUCCESS.AD_DELETED });
			trackEvent('excluirAnuncio', buildAdEvent(activeTab, 'excluirAnuncio', { id: String(deleteModal.adId) }));
		} catch (err) {
			addToast({
				type: 'error',
				title: getUserFacingApiErrorMessage(err, MY_ADS_TOAST.ERROR.DELETE)
			});
		} finally {
			setDeleteModal(INITIAL_DELETE_STATE);
		}
	}, [deleteModal.adId, deleteModal.vehicleType, deleteAdFor, refetch, addToast, trackEvent, activeTab]);

	const runMenuAction = useCallback(
		async (adId: number, action: TMenuActionId) => {
			switch (action) {
			case MENU_IDS.EDIT:
				openEditModal(adId);
				break;
				case MENU_IDS.VIEW: {
					const ad = findAd(adId);
					window.open(getBuyUrl(ad?.buyUrl), '_blank', 'noopener,noreferrer');
					break;
				}
				case MENU_IDS.DEACTIVATE:
					openDeactivateModal(adId);
					break;
				case MENU_IDS.MARK_AS_SOLD: {
					const ad = findAd(adId);
					const adTitle = ad ? `${ad.brandName} ${ad.modelName}` : '';
					const vehicleType: TVehicleType = ad?.vehicleType ?? 'car';
					setMarkAsSoldModal({ isOpen: true, adId, vehicleType, adTitle, isLoading: false });
					break;
				}
				case MENU_IDS.DELETE:
					openDeleteModal(adId);
					break;
			}
		},
		[findAd, openDeactivateModal, openEditModal, openDeleteModal, setMarkAsSoldModal]
	);

	const handleAdAction = useCallback(
		(adId: number, action: TActionId) => dispatchAsync(() => runAdAction(adId, action)),
		[runAdAction]
	);
	const handleMenuAction = useCallback(
		(adId: number, action: TMenuActionId) => dispatchAsync(() => runMenuAction(adId, action)),
		[runMenuAction]
	);
	const confirmDeactivate = useCallback(() => dispatchAsync(runConfirmDeactivate), [runConfirmDeactivate]);
	const confirmMarkAsSold = useCallback(() => dispatchAsync(runConfirmMarkAsSold), [runConfirmMarkAsSold]);
	const handleEditSuccess = useCallback(() => runEditSuccess(), [runEditSuccess]);
	const handleUpgradeSuccess = useCallback(() => runUpgradeSuccess(), [runUpgradeSuccess]);
	const confirmDelete = useCallback(() => dispatchAsync(runConfirmDelete), [runConfirmDelete]);

	return {
		ads,
		total,
		isLoading,
		isListLoading,
		isTabCountsReady,
		isPlaceholderData,
		isUpgradeCheckPending,
		isError,
		isEmpty,
		activeTab,
		setActiveTab: handleTabChange,
		tabCounts,
		refetch,
		handleAdAction,
		handleMenuAction,
		deactivateModal,
		openDeactivateModal,
		closeDeactivateModal,
		confirmDeactivate,
		markAsSoldModal,
		closeMarkAsSoldModal,
		confirmMarkAsSold,
		editModal,
		openEditModal,
		closeEditModal,
		handleEditSuccess,
		upgradeModal,
		closeUpgradeModal,
		handleUpgradeSuccess,
		deleteModal,
		closeDeleteModal,
		confirmDelete,
		isMobile
	};
};

export { useMyAdsPage };
